import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import type { Turn } from '../types/turn';
import type { Task } from '../types/task';

interface ActivitySectionProps {
  task: Task;
  workstreamID: string;
  onTurnCreated?: () => void;
}

const TERMINAL_STATUSES = new Set(['Done', 'Failed']);
const ACTIVITY_POLL_INTERVAL_MS = 5000;

export default function ActivitySection({ task, workstreamID, onTurnCreated }: ActivitySectionProps) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [loading, setLoading] = useState(true);
  const [requestPrompt, setRequestPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const wsQuery = `?workstreamID=${encodeURIComponent(workstreamID)}`;

  const fetchTurns = useCallback(async () => {
    try {
      const resp = await fetch(`/api/tasks/${encodeURIComponent(task.TaskID)}/turns${wsQuery}`);
      if (resp.ok) {
        const data = await resp.json();
        setTurns(data.Turns ?? []);
      }
    } catch {}
    setLoading(false);
  }, [task.TaskID, wsQuery]);

  useEffect(() => {
    setLoading(true);
    fetchTurns();
  }, [fetchTurns]);

  // Always poll so newly-created turns started elsewhere show up without a reload.
  useEffect(() => {
    const id = window.setInterval(() => {
      void fetchTurns();
    }, ACTIVITY_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchTurns]);

  const handleSubmitRequest = useCallback(async () => {
    if (!requestPrompt.trim() || submitting) return;
    setSubmitting(true);
    try {
      const nextTurnID = turns.length > 0
        ? String(Math.max(...turns.map((t) => parseInt(t.TurnID, 10) || 0)) + 1)
        : '1';
      const resp = await fetch(
        `/api/tasks/${encodeURIComponent(task.TaskID)}/turns/${nextTurnID}${wsQuery}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'If-Match': String(task.Version ?? 0),
          },
          body: JSON.stringify({ Prompt: requestPrompt }),
        },
      );
      if (resp.ok) {
        setRequestPrompt('');
        await fetchTurns();
        onTurnCreated?.();
      }
    } catch {} finally {
      setSubmitting(false);
    }
  }, [requestPrompt, submitting, turns, task.TaskID, task.Version, wsQuery, fetchTurns, onTurnCreated]);

  if (loading) {
    return (
      <div style={{ marginTop: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Activity</h3>
        <p style={{ fontSize: '14px', color: '#656d76' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '24px' }}>
      <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>Activity</h3>

      {turns.length === 0 && (
        <p style={{ fontSize: '14px', color: '#656d76', fontStyle: 'italic' }}>
          No activity yet.
        </p>
      )}

      {turns.map((turn) => (
        <div key={turn.TurnID} style={{ marginBottom: '16px' }}>
          {/* Prompt */}
          {turn.Prompt && (
            <div>
              <pre
                style={{
                  background: '#d1dff6',
                  padding: '8px',
                  borderRadius: '4px',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  fontSize: '14px',
                  margin: 0,
                }}
              >
                {turn.Prompt}
              </pre>
              <Link
                className="menu-item-link"
                to={`/workstreams/tasks/${encodeURIComponent(workstreamID)}/${encodeURIComponent(task.TaskID)}/turns/${encodeURIComponent(turn.TurnID)}`}
                style={{ fontSize: '12px', marginTop: '4px', display: 'inline-block' }}
              >
                View Logs
              </Link>
            </div>
          )}

          {/* Running animation */}
          {!TERMINAL_STATUSES.has(turn.Status) && (
            <div className="log-view-activity-indicator" style={{ marginTop: '8px' }}>
              <div className="log-view-activity-dots">
                <div className="log-view-activity-dot" />
                <div className="log-view-activity-dot" />
                <div className="log-view-activity-dot" />
              </div>
              <span>Task running</span>
            </div>
          )}

          {/* Output message */}
          {turn.Status === 'Done' && turn.OutputMessage && (
            <div style={{ marginTop: '8px' }}>
              <div
                style={{
                  background: '#2b2b2b',
                  color: '#d4d4d4',
                  padding: '8px',
                  borderRadius: '4px',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  fontFamily: 'serif',
                  fontSize: '14px',
                }}
              >
                <ReactMarkdown>{turn.OutputMessage}</ReactMarkdown>
              </div>
              <TurnPRLinks turn={turn} task={task} />
            </div>
          )}

          {/* Error message */}
          {turn.Status === 'Failed' && turn.ErrorMessage && (
            <div
              style={{
                background: '#ffebe9',
                border: '1px solid #cf222e',
                borderRadius: '4px',
                color: '#cf222e',
                padding: '8px',
                marginTop: '8px',
                whiteSpace: 'pre-wrap',
                fontSize: '14px',
              }}
            >
              {turn.ErrorMessage}
            </div>
          )}
        </div>
      ))}

      {/* Request Changes box — shown when there's at least one turn */}
      {turns.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <textarea
            ref={textareaRef}
            value={requestPrompt}
            onChange={(e) => setRequestPrompt(e.target.value)}
            placeholder="Click to add a comment ..."
            disabled={submitting}
            rows={3}
            style={{
              width: '100%',
              maxWidth: '800px',
              fontSize: '14px',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '6px',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {requestPrompt.trim() && (
            <div style={{ marginTop: '6px' }}>
              <button
                type="button"
                className="btn-small"
                disabled={submitting}
                onClick={handleSubmitRequest}
              >
                Request Changes
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TurnPRLinks({ turn, task }: { turn: Turn; task: Task }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  // Build PR links with commit-specific URLs if available.
  const prEntries: [string, string][] = [];
  if (task.RepoInfo) {
    for (const [repo, info] of Object.entries(task.RepoInfo)) {
      if (!info.PRLink) continue;
      const commitHash = turn.CommitInfo?.[repo]?.LastCommitHash;
      const url = commitHash ? `${info.PRLink}/commits/${commitHash}` : info.PRLink;
      prEntries.push([repo, url]);
    }
  }
  prEntries.sort(([a], [b]) => a.localeCompare(b));

  if (prEntries.length === 0) return null;

  if (prEntries.length === 1) {
    return (
      <a
        className="menu-item-link"
        href={prEntries[0][1]}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: '12px', marginTop: '4px', display: 'inline-block' }}
      >
        View PR
      </a>
    );
  }

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block', marginTop: '4px' }}>
      <button
        type="button"
        className="btn-small btn-secondary"
        onClick={() => setMenuOpen((o) => !o)}
        style={{ fontSize: '12px' }}
      >
        View PRs
      </button>
      {menuOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '200px',
            overflow: 'hidden',
            zIndex: 1000,
          }}
        >
          {prEntries.map(([repo, link], idx) => (
            <a
              key={repo}
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block',
                padding: '8px 12px',
                textDecoration: 'none',
                color: '#1f2933',
                fontSize: '13px',
                borderTop: idx === 0 ? 'none' : '1px solid #eee',
              }}
            >
              {repo}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
